import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCreateUser, useUpdateUser } from '../../mutations/userMutations';

// ─── Constants ────────────────────────────────────────────────────────────────
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?[0-9\s\-()]{7,20}$/;

const ROLE_OPTIONS = [
  { value: '',               label: 'SELECT ROLE' },
  { value: 'Super Admin',    label: 'Super Admin' },
  { value: 'Admin',          label: 'Admin' },
  { value: 'Limited Admin',  label: 'Limited Admin' },
];

const STATUS_OPTIONS = [
  { value: '',         label: 'SELECT STATUS' },
  { value: 'Active',   label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

// ─── SVGs for Password Toggle ────────────────────────────────────────────────
const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 4 8 4c2.02 0 3.82.668 5.167 1.957A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12 8 12c-2.02 0-3.82-.668-5.167-1.957A13.133 13.133 0 0 1 1.172 8z"/>
    <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
  </svg>
);

const EyeSlashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a18.883 18.883 0 0 0-2.79.208l-.77-.77a18.847 18.847 0 0 1 3.56-.277c2.02 0 3.82.668 5.168 1.957A13.133 13.133 0 0 1 14.828 8a13.125 13.125 0 0 1-3.078 3.038l-.77-.77a11.11 11.11 0 0 0 2.38-2.268zM8 5.5a2.5 2.5 0 1 0 2.242 3.738l-2.242-2.242A2.5 2.5 0 0 0 8 5.5z"/>
    <path d="M10.97 10.97a7.5 7.5 0 0 0 1.073-.707l.86.86a8.966 8.966 0 0 1-1.933 1.073l-.707-1.073zm-2.062 1.343A8.96 8.96 0 0 1 8 12.5c-2.02 0-3.82-.668-5.168-1.957A13.134 13.134 0 0 1 1.172 8c.058-.088.122-.183.195-.288.335-.48.83-1.12 1.465-1.755a8.966 8.966 0 0 1 1.637-1.294l.707 1.073A7.488 7.488 0 0 0 4.108 8a11.11 11.11 0 0 0 2.579 2.457l.77.77zm-.707-3.693l-.707-.707a2.5 2.5 1 0 0 3.536 3.536l-.707-.707a1.5 1.5 0 0 1-2.122-2.122z"/>
    <path d="M12.438 10.468L1.47 1.53l.73-.73 10.968 8.937-.73.73z"/>
  </svg>
);

// ─── Default Values ───────────────────────────────────────────────────────────
const defaultValues = {
  userId: '',
  firstName: '',
  lastName: '',
  displayName: '',
  mobileNumber: '',
  emailId: '',
  password: '',
  confirmPassword: '',
  pandlPassword: '',
  role: '',
  status: '',
};

// ─── Reusable: Controlled Select ──────────────────────────────────────────────
const SelectField = ({ id, label, name, control, rules = {}, options, errors }) => (
  <div className="mb-3 d-flex align-items-center">
    <label htmlFor={id} className="form-label fw-semibold small text-uppercase text-dark mb-0 flex-shrink-0" style={{ width: "125px" }}>
      {label}
      {rules.required && <span className="text-danger ms-1" aria-hidden="true">*</span>}
    </label>
    <div className="flex-grow-1">
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => (
          <select
            {...field}
            id={id}
            className={`form-select${errors ? ' is-invalid' : ''}`}
            aria-label={label}
            aria-invalid={!!errors}
            aria-describedby={errors ? `${id}-err` : undefined}
          >
            {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        )}
      />
      {errors && (
        <div id={`${id}-err`} className="invalid-feedback d-block" role="alert">
          {errors.message}
        </div>
      )}
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const AddUser = () => {
  // ─── Detect Edit vs Create mode ───────────────────────────────────────────
  const { state: routeState } = useLocation();
  const navigate = useNavigate();
  console.log('state', routeState); // ← inspect incoming row data

  // Prefer MongoDB _id; fall back to userId field, then plain id (sample data)
  // Use the database-generated integer id for PUT/DELETE
  const editId = routeState?.id ?? null;
  const isEditMode = !!editId;

  // ─── React Query Mutations ────────────────────────────────────────────────
  const createMutation = useCreateUser();
  const updateMutation = useUpdateUser();

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = useForm({ defaultValues });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPandlPassword, setShowPandlPassword] = useState(false);

  // ─── Pre-fill form when editing ───────────────────────────────────────────
  useEffect(() => {
    if (isEditMode && routeState) {
      reset({
        userId: routeState.userId ?? routeState.id ?? defaultValues.userId,
        firstName: routeState.firstName ?? defaultValues.firstName,
        lastName: routeState.lastName ?? defaultValues.lastName,
        displayName: routeState.displayName ?? defaultValues.displayName,
        mobileNumber: routeState.mobileNumber ?? routeState.mobileNo ?? defaultValues.mobileNumber,
        emailId: routeState.emailId ?? routeState.email ?? defaultValues.emailId,
        password: defaultValues.password,       // never pre-fill passwords
        confirmPassword: defaultValues.confirmPassword,
        pandlPassword: routeState.pandlPassword ?? defaultValues.pandlPassword,
        role: routeState.role ?? defaultValues.role,
        status: routeState.status ?? defaultValues.status,
      });
    }
  }, [isEditMode, routeState, reset]);

  const onSubmit = (data) => {
    console.log('onSubmit data', data); // ← inspect full form payload
    if (isEditMode) {
      // PASSWORD RULE (edit mode):
      //   • Blank → keep the existing value from state (never send "")
      //   • Typed  → use the new value the user entered
      const resolvedPassword = data.password?.trim()
        ? data.password
        : (routeState?.password ?? '');

      // P&L PASSWORD RULE (edit mode): same blank-preservation logic
      const resolvedPandlPassword = data.pandlPassword?.trim()
        ? data.pandlPassword
        : (routeState?.pandlPassword ?? '');

      const payload = { ...data, password: resolvedPassword, pandlPassword: resolvedPandlPassword };

      // UPDATE — PUT /users/:id
      updateMutation.mutate({ id: editId, data: payload }, {
        onSuccess: () => {
          navigate('/admin/users');
        },
      });
    } else {
      // CREATE — POST /users
      createMutation.mutate(data, {
        onSuccess: () => {
          navigate('/admin/users');
        },
      });
    }
  };

  // Combined loading state for the submit button
  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="h-100 d-flex flex-column" noValidate>
      <div className="card shadow-sm border rounded-top-0 rounded-bottom-3 p-3 p-md-4 d-flex flex-column flex-grow-1">
        
        <div className="flex-grow-1">
          <div className="row g-3">
          {/* User ID */}
          <div className="col-12 col-md-6">
            <div className="mb-3 d-flex align-items-center">
              <label htmlFor="userId" className="form-label fw-semibold small text-uppercase text-dark mb-0 flex-shrink-0" style={{ width: "125px" }}>
                User ID <span className="text-danger">*</span>
              </label>
              <div className="flex-grow-1">
                <Controller
                  name="userId"
                  control={control}
                  rules={{ required: 'User ID is required.' }}
                  render={({ field }) => (
                    <input
                      {...field}
                      id="userId"
                      type="text"
                      className={`form-control${errors.userId ? ' is-invalid' : ''}`}
                      placeholder="Enter User ID"
                      aria-invalid={!!errors.userId}
                    />
                  )}
                />
                {errors.userId && <div className="invalid-feedback d-block">{errors.userId.message}</div>}
              </div>
            </div>
          </div>

          {/* Display Name */}
          <div className="col-12 col-md-6">
            <div className="mb-3 d-flex align-items-center">
              <label htmlFor="displayName" className="form-label fw-semibold small text-uppercase text-dark mb-0 flex-shrink-0" style={{ width: "125px" }}>
                Display Name
              </label>
              <div className="flex-grow-1">
                <Controller
                  name="displayName"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      id="displayName"
                      type="text"
                      className="form-control"
                      placeholder="Enter Display Name"
                    />
                  )}
                />
              </div>
            </div>
          </div>

          {/* First Name */}
          <div className="col-12 col-md-6">
            <div className="mb-3 d-flex align-items-center">
              <label htmlFor="firstName" className="form-label fw-semibold small text-uppercase text-dark mb-0 flex-shrink-0" style={{ width: "125px" }}>
                First Name <span className="text-danger">*</span>
              </label>
              <div className="flex-grow-1">
                <Controller
                  name="firstName"
                  control={control}
                  rules={{ required: 'First name is required.' }}
                  render={({ field }) => (
                    <input
                      {...field}
                      id="firstName"
                      type="text"
                      className={`form-control${errors.firstName ? ' is-invalid' : ''}`}
                      placeholder="Enter First Name"
                      aria-invalid={!!errors.firstName}
                    />
                  )}
                />
                {errors.firstName && <div className="invalid-feedback d-block">{errors.firstName.message}</div>}
              </div>
            </div>
          </div>

          {/* Last Name */}
          <div className="col-12 col-md-6">
            <div className="mb-3 d-flex align-items-center">
              <label htmlFor="lastName" className="form-label fw-semibold small text-uppercase text-dark mb-0 flex-shrink-0" style={{ width: "125px" }}>
                Last Name
              </label>
              <div className="flex-grow-1">
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      id="lastName"
                      type="text"
                      className="form-control"
                      placeholder="Enter Last Name"
                    />
                  )}
                />
              </div>
            </div>
          </div>

          {/* Mobile Number */}
          <div className="col-12 col-md-6">
            <div className="mb-3 d-flex align-items-center">
              <label htmlFor="mobileNumber" className="form-label fw-semibold small text-uppercase text-dark mb-0 flex-shrink-0" style={{ width: "125px" }}>
                Mobile No <span className="text-danger">*</span>
              </label>
              <div className="flex-grow-1">
                <Controller
                  name="mobileNumber"
                  control={control}
                  rules={{
                    required: 'Mobile number is required.',
                    pattern: { value: PHONE_REGEX, message: 'Please enter a valid mobile number with country code.' },
                  }}
                  render={({ field }) => (
                    <input
                      {...field}
                      id="mobileNumber"
                      type="tel"
                      className={`form-control${errors.mobileNumber ? ' is-invalid' : ''}`}
                      placeholder="e.g. +91 9876543210"
                      aria-invalid={!!errors.mobileNumber}
                    />
                  )}
                />
                {errors.mobileNumber && <div className="invalid-feedback d-block">{errors.mobileNumber.message}</div>}
              </div>
            </div>
          </div>

          {/* Email ID */}
          <div className="col-12 col-md-6">
            <div className="mb-3 d-flex align-items-center">
              <label htmlFor="emailId" className="form-label fw-semibold small text-uppercase text-dark mb-0 flex-shrink-0" style={{ width: "125px" }}>
                Email ID <span className="text-danger">*</span>
              </label>
              <div className="flex-grow-1">
                <Controller
                  name="emailId"
                  control={control}
                  rules={{
                    required: 'Email ID is required.',
                    pattern: { value: EMAIL_REGEX, message: 'Enter a valid email.' },
                  }}
                  render={({ field }) => (
                    <input
                      {...field}
                      id="emailId"
                      type="email"
                      className={`form-control${errors.emailId ? ' is-invalid' : ''}`}
                      placeholder="example@email.com"
                      aria-invalid={!!errors.emailId}
                    />
                  )}
                />
                {errors.emailId && <div className="invalid-feedback d-block">{errors.emailId.message}</div>}
              </div>
            </div>
          </div>

          {/* Password — skip required validation in edit mode */}
          <div className="col-12 col-md-6">
            <div className="mb-3 d-flex align-items-center">
              <label htmlFor="password" className="form-label fw-semibold small text-uppercase text-dark mb-0 flex-shrink-0" style={{ width: "125px" }}>
                Password {!isEditMode && <span className="text-danger">*</span>}
              </label>
              <div className="flex-grow-1">
                <div className="input-group">
                  <Controller
                    name="password"
                    control={control}
                    rules={!isEditMode ? {
                      required: 'Password is required.',
                      minLength: { value: 6, message: 'Password must be at least 6 characters.' },
                    } : {}}
                    render={({ field }) => (
                      <input
                        {...field}
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        className={`form-control${errors.password ? ' is-invalid' : ''}`}
                        placeholder={isEditMode ? 'Leave blank to keep current' : '••••••••'}
                        aria-invalid={!!errors.password}
                      />
                    )}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary d-flex align-items-center"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                  </button>
                </div>
                {errors.password && <div className="invalid-feedback d-block mt-1">{errors.password.message}</div>}
              </div>
            </div>
          </div>

          {/* Role */}
          <div className="col-12 col-md-6">
            <SelectField
              id="role"
              label="Role"
              name="role"
              control={control}
              options={ROLE_OPTIONS}
              rules={{ required: 'Role is required.' }}
              errors={errors.role}
            />
          </div>

          {/* Confirm Password */}
          <div className="col-12 col-md-6">
            <div className="mb-3 d-flex align-items-center">
              <label htmlFor="confirmPassword" className="form-label fw-semibold small text-uppercase text-dark mb-0 flex-shrink-0" style={{ width: "125px" }}>
                Confirm PWD {!isEditMode && <span className="text-danger">*</span>}
              </label>
              <div className="flex-grow-1">
                <div className="input-group">
                  <Controller
                    name="confirmPassword"
                    control={control}
                    rules={!isEditMode ? {
                      required: 'Confirm password is required.',
                      validate: (val) => val === getValues('password') || 'Passwords do not match.',
                    } : {}}
                    render={({ field }) => (
                      <input
                        {...field}
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        className={`form-control${errors.confirmPassword ? ' is-invalid' : ''}`}
                        placeholder={isEditMode ? 'Leave blank to keep current' : '••••••••'}
                        aria-invalid={!!errors.confirmPassword}
                      />
                    )}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary d-flex align-items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? <EyeSlashIcon /> : <EyeIcon />}
                  </button>
                </div>
                {errors.confirmPassword && <div className="invalid-feedback d-block mt-1">{errors.confirmPassword.message}</div>}
              </div>
            </div>
          </div>

          {/* P&L Password */}
          <div className="col-12 col-md-6">
            <div className="mb-3 d-flex align-items-center">
              <label htmlFor="pandlPassword" className="form-label fw-semibold small text-uppercase text-dark mb-0 flex-shrink-0" style={{ width: "125px" }}>
                P&amp;L Password
              </label>
              <div className="flex-grow-1">
                <div className="input-group">
                  <Controller
                    name="pandlPassword"
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        id="pandlPassword"
                        type={showPandlPassword ? 'text' : 'password'}
                        className="form-control"
                        placeholder="Enter P&L password"
                      />
                    )}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary d-flex align-items-center"
                    onClick={() => setShowPandlPassword(!showPandlPassword)}
                    aria-label={showPandlPassword ? 'Hide P&L password' : 'Show P&L password'}
                  >
                    {showPandlPassword ? <EyeSlashIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="col-12 col-md-6">
            <SelectField
              id="status"
              label="Status"
              name="status"
              control={control}
              options={STATUS_OPTIONS}
              rules={{ required: 'Status is required.' }}
              errors={errors.status}
            />
          </div>
          </div>
        </div>

        {/* Submit */}
        <div className="text-center pt-4 pb-2 mt-auto">
          <button
            type="submit"
            className="btn px-5 py-2 fw-semibold text-white gtss-bg-primary border-0"
            disabled={isPending}
            aria-label="Submit user form"
            style={{ borderRadius: "20px" }}
          >
            {isPending ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                Saving...
              </>
            ) : isEditMode ? (
              'Update User'
            ) : (
              'Submit'
            )}
          </button>
        </div>

      </div>
    </form>
  );
};

export default AddUser;
